import { useEffect, useRef, useState } from 'react';
import { CORE_BAND_LEVEL, ITEMS, THREAT_BAND_LEVEL, type CoreBand, type ThreatBand } from '@qoobd/rules';
import { audio } from '../audio/engine';
import { game } from '../game/controller';
import { leaveOnline } from '../game/online';
import { savePrefs, useGame, type Atmosphere, type GameState } from '../game/store';

type GameStateToast = GameState['toast'];
import { ItemIcon } from './icons';
import { MiniCube } from './MiniCube';

const pad2 = (n: number) => String(n).padStart(2, '0');
export const formatTime = (ms: number) => `${Math.floor(ms / 60000)}:${pad2(Math.floor(ms / 1000) % 60)}`;

const THREAT_LABEL: Record<ThreatBand, string> = { none: 'CLEAR', detected: 'DETECTED', near: 'NEAR', danger: 'DANGER CLOSE' };
const CORE_PULSE: Record<Exclude<CoreBand, 'none'>, number> = { faint: 1500, warm: 800, hot: 380 };
const THREAT_PULSE: Record<Exclude<ThreatBand, 'none'>, number> = { detected: 1200, near: 700, danger: 330 };

/** Restart a one-shot CSS animation whenever `trigger` changes. */
function useFlash(trigger: number): string {
  const [on, setOn] = useState(false);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setOn(false);
    const id = requestAnimationFrame(() => setOn(true));
    return () => cancelAnimationFrame(id);
  }, [trigger]);
  return on ? ' go' : '';
}

/** A sensor's ping dot and sound, pulsing faster as the band rises. */
function usePulse(active: boolean, periodMs: number, onPulse: () => void): number {
  const [count, setCount] = useState(0);
  const cb = useRef(onPulse);
  cb.current = onPulse;
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      cb.current();
      setCount((c) => c + 1);
    }, periodMs);
    return () => clearInterval(id);
  }, [active, periodMs]);
  return count;
}

function Sensor({ kind }: { kind: 'core' | 'threat' }) {
  const coreBand = useGame((s) => s.coreBand);
  const threatBand = useGame((s) => s.threatBand);
  const playing = useGame((s) => s.phase === 'playing');
  const jammed = useGame((s) => s.jammed);
  const band = kind === 'core' ? coreBand : threatBand;
  const level = kind === 'core' ? CORE_BAND_LEVEL[coreBand] : THREAT_BAND_LEVEL[threatBand];
  const period = band === 'none' ? 0 : kind === 'core' ? CORE_PULSE[band as Exclude<CoreBand, 'none'>] : THREAT_PULSE[band as Exclude<ThreatBand, 'none'>];
  const pulses = usePulse(playing && !jammed && band !== 'none', period, () =>
    kind === 'core' ? audio.coreChime(coreBand as Exclude<CoreBand, 'none'>) : audio.threatBeep(threatBand as Exclude<ThreatBand, 'none'>),
  );
  const ping = useFlash(pulses);
  const label = jammed ? 'STATIC' : kind === 'core' ? (band === 'none' ? 'NO SIGNAL' : band.toUpperCase()) : THREAT_LABEL[threatBand];
  return (
    <div className={`panel sensor ${kind}${jammed ? ' jammed' : ''}${band !== 'none' && !jammed ? ' live' : ''}`}>
      <div className="lbl">
        {kind === 'core' ? 'Core' : 'Threat'}
        <span className="lbl-long"> sensor</span>
        <span className="lbl-extra">{kind === 'core' ? ' · 4.0 rooms' : ' · 2.5 rooms'}</span>
      </div>
      <div className="sensor-row">
        <div className={`ping${ping}`} />
        <div className={`band${band === 'none' ? '' : ' on'}`} aria-live="polite">
          {label}
        </div>
      </div>
      <div className="segs">
        {[0, 1, 2].map((i) => (
          <i key={i} className={i < level ? 'on' : ''} />
        ))}
      </div>
    </div>
  );
}

function LockIndicator() {
  const lock = useGame((s) => s.lock);
  const flash = useFlash(useGame((s) => s.lockFlash));
  const [, force] = useState(0);
  const now = performance.now();
  const active = lock && now < lock.startedAt + lock.durationMs;
  useEffect(() => {
    if (!lock) return;
    const remaining = lock.startedAt + lock.durationMs - performance.now();
    if (remaining <= 0) return;
    const id = setTimeout(() => force((n) => n + 1), remaining + 10);
    return () => clearTimeout(id);
  }, [lock]);
  return (
    <div className="hud lockwrap">
      <div className={`lock${flash}`}>{active ? lock.label : 'READY'}</div>
      <div className={`lockbar${flash}`}>
        <i
          key={lock ? lock.startedAt : 0}
          style={active ? { animation: `fill ${lock.durationMs}ms linear forwards` } : { width: 0 }}
        />
      </div>
    </div>
  );
}

function Clock() {
  const startedAt = useGame((s) => s.startedAt);
  const playing = useGame((s) => s.phase === 'playing');
  const [now, setNow] = useState(performance.now());
  useEffect(() => {
    if (!playing || startedAt === null) return;
    const id = setInterval(() => setNow(performance.now()), 250);
    return () => clearInterval(id);
  }, [playing, startedAt]);
  return <span>{startedAt === null ? '0:00' : formatTime(now - startedAt)}</span>;
}

/**
 * While nothing needs attention (both sensors quiet, no static, rival out of sight, no message),
 * the HUD settles to half strength on touch screens; any of those brings it straight back.
 */
function useQuiet(): boolean {
  const threat = useGame((s) => s.threatBand);
  const core = useGame((s) => s.coreBand);
  const jammed = useGame((s) => s.jammed);
  const rival = useGame((s) => s.rivalVisible);
  const toast = useGame((s) => s.toast);
  const playing = useGame((s) => s.phase === 'playing');
  const busy = !playing || threat !== 'none' || core !== 'none' || jammed || rival;
  const [quiet, setQuiet] = useState(false);
  useEffect(() => {
    if (busy) {
      setQuiet(false);
      return;
    }
    setQuiet(false);
    const id = setTimeout(() => setQuiet(true), 2500);
    return () => clearTimeout(id);
  }, [busy, toast]);
  useEffect(() => {
    document.documentElement.classList.toggle('hud-quiet', quiet);
    return () => document.documentElement.classList.remove('hud-quiet');
  }, [quiet]);
  return quiet;
}

const ATMOS_ORDER: Atmosphere[] = ['normal', 'subtle', 'off'];

/** Sound, atmosphere, touch controls, new cube and back to title, behind one button. */
function Menu() {
  const [open, setOpen] = useState(false);
  const soundOn = useGame((s) => s.soundOn);
  const atmosphere = useGame((s) => s.atmosphere);
  const padMode = useGame((s) => s.padMode);
  const tier = useGame((s) => s.tier);
  const online = useGame((s) => s.mode === 'online');
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // While the menu is open the HUD stays at full strength and above the thumb cluster.
    document.documentElement.classList.toggle('menu-open', open);
    return () => document.documentElement.classList.remove('menu-open');
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('pointerdown', away);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('pointerdown', away);
      window.removeEventListener('keydown', esc);
    };
  }, [open]);
  const set = (patch: Partial<GameState>) => {
    useGame.setState(patch);
    savePrefs();
  };
  return (
    <div className="menu" ref={wrap}>
      <button className="menu-btn" type="button" aria-expanded={open} aria-haspopup="true" aria-label="Menu" onClick={(e) => (e.currentTarget.blur(), setOpen(!open))}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>
      {open && (
        <div className="menu-pop" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              audio.setSound(!soundOn);
              set({ soundOn: !soundOn });
            }}
          >
            Sound <b>{soundOn ? 'On' : 'Off'}</b>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const next = ATMOS_ORDER[(ATMOS_ORDER.indexOf(atmosphere) + 1) % ATMOS_ORDER.length]!;
              audio.setAtmosphere(next);
              set({ atmosphere: next });
            }}
          >
            Atmosphere <b>{atmosphere[0]!.toUpperCase() + atmosphere.slice(1)}</b>
          </button>
          <button type="button" role="menuitem" className="touch-only" onClick={() => set({ padMode: padMode === 'pad' ? 'gestures' : 'pad' })}>
            Touch controls <b>{padMode === 'pad' ? 'Pad' : 'Gestures only'}</b>
          </button>
          {online ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                void leaveOnline();
                useGame.setState({ phase: 'intro', mode: 'play' });
              }}
            >
              Leave match <b>Forfeits</b>
            </button>
          ) : (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  game.start(tier);
                }}
              >
                New cube
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  useGame.setState({ phase: 'intro' });
                }}
              >
                Back to title
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function Hud() {
  const s = useGame();
  const bonk = useFlash(s.bonkFlash);
  const cloak = useFlash(s.cloakFlash);
  const [toast, setToast] = useState<GameStateToast>(null);
  const [fullReadout, setFullReadout] = useState(false);
  useQuiet();
  useEffect(() => {
    if (!s.toast) return;
    setToast(s.toast);
    const id = setTimeout(() => setToast(null), 1600);
    return () => clearTimeout(id);
  }, [s.toast]);

  return (
    <>
      <div className={`flash${bonk}`} />
      <div className={`shimmer${cloak}`} />
      {s.jammed && <div className="static" aria-hidden="true" />}
      {toast && (
        <div className="toast" role="status" key={toast.id}>
          {toast.text}
          <small>{toast.sub}</small>
        </div>
      )}

      <div className="hud tl">
        <div className="panel brandpanel">
          <div className="brandrow">
            <div className="brand">
              Q<b>OO</b>BD
            </div>
            <Menu />
          </div>
          <button
            type="button"
            className={`readout${fullReadout ? ' full' : ''}`}
            aria-label="Position and orientation; tap for coordinates"
            onClick={(e) => (e.currentTarget.blur(), setFullReadout(!fullReadout))}
          >
            <span className="coords">
              [X {pad2(s.coords[0])} | Y {pad2(s.coords[1])} | Z {pad2(s.coords[2])}]
            </span>
            <span className="orient">
              FACING {s.facing} // UP {s.up}
            </span>
          </button>
        </div>
        <div className="panel itempanel">
          <div className="lbl itemlbl">
            Your item<span className="lbl-extra"> · hidden from rival</span>
          </div>
          <div className="itemrow">
            <div className="item-now">
              <ItemIcon item={s.item} />
              <span className="item-name">{s.item.toUpperCase()}</span>
            </div>
            <div className="swaps" role="group" aria-label="Swap item">
              {ITEMS.map((item, i) => (
                <button
                  key={item}
                  type="button"
                  aria-pressed={s.item === item}
                  aria-label={`Swap to ${item}`}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    game.input({ kind: 'swap', item });
                  }}
                >
                  <ItemIcon item={item} size={14} />
                  <span className="swap-name">{item[0]!.toUpperCase() + item.slice(1)}</span>
                  <kbd>{i + 1}</kbd>
                </button>
              ))}
            </div>
          </div>
          <div className="gear" role="group" aria-label="Tactical gear">
            <button
              type="button"
              disabled={s.emp < 1}
              aria-label={`Fire EMP, ${s.emp} left`}
              onPointerDown={(e) => {
                e.preventDefault();
                game.input({ kind: 'emp' });
              }}
            >
              EMP <b>×{s.emp}</b> <kbd>R</kbd>
            </button>
            <button
              type="button"
              disabled={s.bulkhead < 1}
              aria-label={`Seal the door behind you, ${s.bulkhead} left`}
              onPointerDown={(e) => {
                e.preventDefault();
                game.input({ kind: 'bulkhead' });
              }}
            >
              SEAL <b>×{s.bulkhead}</b> <kbd>F</kbd>
            </button>
          </div>
        </div>
        <div className="panel stats">
          <div>
            <span className="lbl">Steps</span>
            <span>{s.steps}</span>
          </div>
          <div>
            <span className="lbl">Actions</span>
            <span>{s.actions}</span>
          </div>
          <div>
            <span className="lbl">Bonks</span>
            <span>{s.bonks}</span>
          </div>
          <div>
            <span className="lbl">Time</span>
            <Clock />
          </div>
        </div>
      </div>

      <div className="hud tr">
        <Sensor kind="threat" />
        <Sensor kind="core" />
      </div>

      <div className="hud bl panel minipanel">
        <div className="lbl">Explored · mini-cube</div>
        <MiniCube />
      </div>

      <LockIndicator />
    </>
  );
}
